/**
 * The companies an applicant can name when prestige is one of their top
 * priorities — the named piles under it, and the search that reaches the rest.
 *
 * A CURATED list rather than a read of the `Company` table, on purpose. The
 * ticket's reversibility constraints say the priority answers must stay cheap
 * to delete and must have nothing downstream depending on them; a stored
 * answer keyed to rows in a live table is a dependency in the other direction,
 * and a company soft-deleted out of that table would render in the admin recap
 * as a bare identifier. This list is small, static, and ours. Swapping it for a
 * search over the real table later is a change of source, not of storage — the
 * ids here are slugs of the same shape that table already uses.
 *
 * IDS, NEVER LABELS, the same fence the ladder and the follow-up piles put
 * around their own answers: `slug` is what is stored and `name` is only ever
 * drawn, so renaming a company never rewrites an answer somebody already gave.
 */

/** One company, as a chip. */
export interface Company {
  /** Stored. Chosen to match the `Company` table's own slug convention. */
  slug: string
  /** Drawn, and what the admin recap prints. */
  name: string
}

/**
 * Shorthand, because the list below is long and two fields of noise per line
 * is what makes a long list stop being readable.
 */
const co = (slug: string, name: string): Company => ({ slug, name })

/**
 * Every company we offer, in one list.
 *
 * The piles under prestige draw from this same list rather than carrying
 * chips of their own, so a company named in the FAANG pile and a company
 * found by the search are the same stored id. Two parallel spellings of
 * "Google" is how an admin recap ends up reporting one answer twice.
 *
 * Alphabetical, which is the only order that makes adding one to the right
 * place obvious. Nothing reads the order — the piles name their members and
 * the search ranks by what was typed.
 */
export const COMPANIES: readonly Company[] = [
  co('adobe', 'Adobe'),
  co('affirm', 'Affirm'),
  co('airbnb', 'Airbnb'),
  co('airtable', 'Airtable'),
  co('amazon', 'Amazon'),
  co('anthropic', 'Anthropic'),
  co('apple', 'Apple'),
  co('atlassian', 'Atlassian'),
  co('block', 'Block'),
  co('bloomberg', 'Bloomberg'),
  co('canva', 'Canva'),
  co('cloudflare', 'Cloudflare'),
  co('cohere', 'Cohere'),
  co('coinbase', 'Coinbase'),
  co('databricks', 'Databricks'),
  co('datadog', 'Datadog'),
  co('doordash', 'DoorDash'),
  co('dropbox', 'Dropbox'),
  co('duolingo', 'Duolingo'),
  co('figma', 'Figma'),
  co('github', 'GitHub'),
  co('google', 'Google'),
  co('instacart', 'Instacart'),
  co('intuit', 'Intuit'),
  co('linkedin', 'LinkedIn'),
  co('lyft', 'Lyft'),
  co('meta', 'Meta'),
  co('microsoft', 'Microsoft'),
  co('mistral', 'Mistral'),
  co('mongodb', 'MongoDB'),
  co('netflix', 'Netflix'),
  co('notion', 'Notion'),
  co('nvidia', 'Nvidia'),
  co('openai', 'OpenAI'),
  co('oracle', 'Oracle'),
  co('palantir', 'Palantir'),
  co('paypal', 'PayPal'),
  co('pinterest', 'Pinterest'),
  co('plaid', 'Plaid'),
  co('ramp', 'Ramp'),
  co('reddit', 'Reddit'),
  co('roblox', 'Roblox'),
  co('salesforce', 'Salesforce'),
  co('scale-ai', 'Scale AI'),
  co('shopify', 'Shopify'),
  co('snap', 'Snap'),
  co('snowflake', 'Snowflake'),
  co('spacex', 'SpaceX'),
  co('spotify', 'Spotify'),
  co('stripe', 'Stripe'),
  co('tesla', 'Tesla'),
  co('tiktok', 'TikTok'),
  co('twilio', 'Twilio'),
  co('uber', 'Uber'),
  co('vercel', 'Vercel'),
  co('waymo', 'Waymo'),
  co('workday', 'Workday'),
  co('xai', 'xAI'),
  co('zoom', 'Zoom'),
]

/** How many matches a search shows at once. */
export const COMPANY_HITS = 6

/** Whether a stored id is a company we still offer. */
export function isCompanySlug(slug: string): boolean {
  return COMPANIES.some((c) => c.slug === slug)
}

/**
 * A company as display text; a slug we no longer carry passes straight
 * through, the same way `priorityLabel` and `followUpOptionLabel` do.
 *
 * The tolerant read path the ticket asks for: dropping a company from the
 * list must not blank the answer of somebody who already named it.
 */
export function companyName(slug: string): string {
  return COMPANIES.find((c) => c.slug === slug)?.name ?? slug
}

/**
 * The monogram drawn on a company chip.
 *
 * DERIVED rather than stored beside the name, because a mark that can drift
 * from the name it stands for is a mark that eventually does. The design
 * prototype pairs each monogram with that company's own brand colour; this
 * ships the disc in the page's own neutral instead — the ticket flags that
 * forty-odd competitor brand colours in a system whose accent budget is one
 * teal would be the loudest thing on the page, and it is right.
 */
export function companyMark(slug: string): string {
  return companyName(slug).charAt(0).toUpperCase()
}

/**
 * Companies matching what has been typed, closest first.
 *
 * Every match, not the first few: the caller draws `COMPANY_HITS` of them and
 * needs the total to say "closest 6 of 14", which is the difference between a
 * list that looks complete and one that says to keep typing.
 *
 * Substring rather than prefix: somebody typing "AI" is looking for Scale AI
 * and xAI, and a prefix match finds neither. A name that STARTS with the
 * query still sorts above one that merely contains it, so typing "sn" puts
 * Snap and Snowflake above nothing and typing "ai" does not bury xAI under
 * companies that happen to have those two letters in the middle.
 *
 * `exclude` is what keeps the search from offering a company that is already
 * on screen — one that has its own chip in a pile under the same priority, or
 * one they have already picked. The same company drawn twice is an answer
 * that can be given twice and printed twice.
 */
export function searchCompanies(
  query: string,
  exclude: readonly string[] = [],
): Company[] {
  const q = query.trim().toLowerCase()
  if (q === '') return []
  const hits = COMPANIES.filter(
    (c) => !exclude.includes(c.slug) && c.name.toLowerCase().includes(q),
  )
  return hits
    .filter((c) => c.name.toLowerCase().startsWith(q))
    .concat(hits.filter((c) => !c.name.toLowerCase().startsWith(q)))
}
